import Stack from "@mui/material/Stack";
import { GomakePasswordInput } from "@/components";
import { useTranslation } from "react-i18next";
import { SecondaryButton } from "@/components/button/secondary-button";
import { useState, useMemo } from "react";
import { ErrorIcon } from "./error-icon";
import {
  EMPLOYEE_STRONG_PASSWORD_REGEX_STRING,
  isEmployeePasswordStrong,
} from "@/utils/employee-password-policy";

const labelStyle: React.CSSProperties = {
  fontFamily: "var(--theme-font-family-brand, 'Forma DJR UI', sans-serif)",
  fontStyle: "normal",
  fontWeight: 400,
  fontSize: 16,
  lineHeight: "20px",
  color: "#3D3D3D",
  display: "flex",
  alignItems: "center",
};

const helperTextStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: "4px",
  fontFamily: "var(--theme-font-family-brand, 'Forma DJR UI', sans-serif)",
  fontStyle: "normal",
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "20px",
  color: "#1A1A1A",
};

interface IChangePasswordProps {
  onChangePassword: (
    currentPass: string,
    newPass: string,
    confirmPass: string,
    onError?: (message?: string) => void,
  ) => void;
}

const ChangePasswordComponent = ({
  onChangePassword,
}: IChangePasswordProps) => {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [currentPasswordError, setCurrentPasswordError] = useState<string>("");

  const validationErrors = useMemo(() => {
    const errors = {
      newPassword: "",
      confirmPassword: "",
    };
    if (!newPassword && !confirmPassword) return errors;
    if (newPassword && currentPassword && newPassword === currentPassword) {
      errors.newPassword = t("profileSettings.newPasswordSameAsCurrent");
    } else if (
      newPassword.length > 0 &&
      !isEmployeePasswordStrong(newPassword)
    ) {
      errors.newPassword = t("usersSettings.passwordNotStrongEnough");
    }
    if (confirmPassword && newPassword !== confirmPassword) {
      errors.confirmPassword = t("profileSettings.passwordsMismatch");
    }
    return errors;
  }, [currentPassword, newPassword, confirmPassword, t]);

  const allFilled =
    !!currentPassword.trim() &&
    !!newPassword.trim() &&
    !!confirmPassword.trim();

  const isFormValid =
    allFilled &&
    !currentPasswordError &&
    !validationErrors.newPassword &&
    !validationErrors.confirmPassword;

  const handleSubmit = () => {
    setCurrentPasswordError("");
    if (!isFormValid) return;
    onChangePassword(currentPassword, newPassword, confirmPassword, (msg) => {
      setCurrentPasswordError(
        msg && msg.length > 0
          ? msg
          : t("profileSettings.incorrectCurrentPassword"),
      );
    });
  };

  const renderHelper = (message: string) =>
    message ? (
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ ...helperTextStyle, marginTop: 0.5 }}
      >
        <ErrorIcon />
        <span>{message}</span>
      </Stack>
    ) : null;

  return (
    <Stack
      direction="column"
      alignItems="stretch"
      sx={{
        padding: "20px 0 0",
        width: "100%",
        borderRadius: 0,
      }}
    >
      <Stack
        direction="column"
        gap={"32px"}
        sx={{
          padding: "16px 40px 40px",
          width: "100%",
        }}
      >
        <Stack
          direction="column"
          gap={"6px"}
          sx={{ width: "100%", minWidth: 0, alignItems: "stretch" }}
        >
          <label style={labelStyle}>
            {t("profileSettings.currentPassword")}
          </label>
          <GomakePasswordInput
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              if (currentPasswordError) setCurrentPasswordError("");
            }}
            inputId="change-password-current"
            name="current-password"
            feedback={false}
            toggleMask
            autoComplete="current-password"
            placeholder={t("profileSettings.currentPassword")}
            error={!!currentPasswordError}
          />
          {renderHelper(currentPasswordError)}
        </Stack>
        <Stack direction="column" gap={"6px"} sx={{ width: "100%" }}>
          <label style={labelStyle}>{t("profileSettings.newPassword")}</label>
          <Stack
            direction="column"
            gap={0.5}
            sx={{ width: "100%", minWidth: 0, alignItems: "stretch" }}
          >
            <GomakePasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              inputId="change-password-new"
              name="new-password"
              feedback
              appendTo="self"
              strongRegex={EMPLOYEE_STRONG_PASSWORD_REGEX_STRING}
              promptLabel={t("usersSettings.passwordMeterPrompt")}
              weakLabel={t("usersSettings.passwordMeterWeak")}
              mediumLabel={t("usersSettings.passwordMeterMedium")}
              strongLabel={t("usersSettings.passwordMeterStrong")}
              header={
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    marginBottom: 4,
                    maxWidth: "100%",
                    overflowWrap: "break-word",
                  }}
                >
                  {t("usersSettings.passwordPolicyTitle")}
                </div>
              }
              footer={
                <div
                  style={{
                    fontSize: 12,
                    color: "#666",
                    marginTop: 10,
                    lineHeight: 1.45,
                    maxWidth: "100%",
                    overflowWrap: "break-word",
                  }}
                >
                  {t("usersSettings.passwordPolicyFooter")}
                </div>
              }
              toggleMask
              autoComplete="new-password"
              placeholder={t("profileSettings.newPassword")}
              error={
                !!validationErrors.newPassword ||
                (newPassword.length > 0 &&
                  !isEmployeePasswordStrong(newPassword))
              }
            />
            {renderHelper(validationErrors.newPassword)}
          </Stack>
        </Stack>
        <Stack
          direction="column"
          gap={"6px"}
          sx={{ width: "100%", minWidth: 0, alignItems: "stretch" }}
        >
          <label style={labelStyle}>
            {t("profileSettings.confirmNewPassword")}
          </label>
          <GomakePasswordInput
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            inputId="change-password-confirm"
            name="confirm-new-password"
            feedback={false}
            toggleMask
            autoComplete="new-password"
            placeholder={t("profileSettings.confirmNewPassword")}
            error={!!validationErrors.confirmPassword}
          />
          {renderHelper(validationErrors.confirmPassword)}
        </Stack>
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="flex-end"
        gap={"16px"}
        sx={{
          padding: "32px 40px 0",
          width: "100%",
        }}
      >
        <SecondaryButton
          onClick={handleSubmit}
          variant={"contained"}
          themeAsPrimary
          disabled={!isFormValid}
          style={{ width: "fit-content", minWidth: 170 }}
        >
          {t("profileSettings.changePassword")}
        </SecondaryButton>
      </Stack>
    </Stack>
  );
};

export { ChangePasswordComponent };
